/**
 * OrgBanner — card shown on the hosting dashboard when user belongs to an org.
 * Shows org logo/initials, name, verified badge, and member count.
 */
import { View, Text } from 'react-native'
import Animated from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { Building2, Users, ShieldCheck, ChevronRight } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Badge } from '@/components/ui/Badge'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import type { Entrance } from '@/lib/hooks/use-entrance'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import type { Organization } from '@/lib/types/organization.types'

interface OrgBannerProps {
  organization: Organization
  onPress: () => void
  /**
   * Entrance handed down by the screen. The banner sits inside a region that
   * remounts, so it must not own the latch — see `useEntrance`.
   */
  entering?: Entrance
}

export function OrgBanner({ organization, onPress, entering }: OrgBannerProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Animated.View collapsable={false} entering={entering}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={`${organization.name} — ${t('org.dashboard')}`}
        testID="hosting.org-banner"
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            backgroundColor: isDark ? colors.surfaceRaised : colors.surface,
            gap: SPACING.md,
            ...WEB_SHADOWS.sm,
          }}
        >
          {/* Org icon / logo */}
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: RADIUS.md,
              backgroundColor: `${COLORS.purple.DEFAULT}12`,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Building2 size={24} color={COLORS.purple.DEFAULT} strokeWidth={1.5} />
          </View>

          {/* Name + meta */}
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
              <Text
                style={{
                  fontSize: 16,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  flexShrink: 1,
                }}
                numberOfLines={1}
              >
                {organization.name}
              </Text>
              {organization.isVerified && (
                <ShieldCheck size={16} color={colors.success} strokeWidth={2} />
              )}
            </View>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                marginTop: SPACING.xs,
              }}
            >
              {organization.memberCount !== undefined && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
                  <Users size={13} color={colors.textMuted} strokeWidth={1.8} />
                  <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textMuted }}>
                    {organization.memberCount}
                  </Text>
                </View>
              )}
              {!organization.isVerified && (
                <Badge label={t('org.verification_pending')} variant="warning" size="sm" />
              )}
            </View>
          </View>

          {/* Chevron */}
          <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.5} />
        </View>
      </Pressable>
    </Animated.View>
  )
}
